import { useState } from "react";
import { useRouteMatch } from "react-router-dom";
import useTeamMembers, { TUserData } from "../hooks/useUserApprovals";
import { Loader } from "../../../../../../../UI/Loaders/Loaders";
import { ApiErrorState } from "../../../../../../../interfaces/response";
import { MDBTable, MDBTableBody, MDBTableHead } from "mdbreact";
import { useDispatch } from "react-redux";
import { showModal } from "../../../../../../../redux/actions/modal";
import styles from "./UserApprovals.module.scss";

export enum UserStatus {
  APPROVED = "Approved",
  PENDING_DIRECTOR_APPROVAL = "PendingDirectorApproval",
  PENDING_BANK_APPROVAL = "PendingBankApproval",
  REJECTED = "Rejected",
  INACTIVE = "Inactive",
  PENDING_DIRECTOR_REACTIVATION = "PendingDirectorReactivation",
  PENDING_BANK_REACTIVATION = "PendingBankReactivation",
}

const renderRoles = (user: TUserData) => {
  const roles = user?.currentRoles?.length
    ? user.currentRoles
    : user?.rolesRequestedForApproval?.length
      ? user.rolesRequestedForApproval
      : null;

  if (!roles) return null;

  return <p className="">{roles.join(", ")}</p>;
};

const renderStatus = (status?: UserStatus) => {
  if (!status) return <span className="status-badge unknown">Unknown</span>;

  const labelMap: Record<UserStatus, string> = {
    [UserStatus.APPROVED]: "Approved",
    [UserStatus.PENDING_DIRECTOR_APPROVAL]: "Pending Director Approval",
    [UserStatus.PENDING_BANK_APPROVAL]: "Pending Bank Approval",
    [UserStatus.REJECTED]: "Rejected",
    [UserStatus.INACTIVE]: "Inactive",
    [UserStatus.PENDING_DIRECTOR_REACTIVATION]: "Pending Director Reactivation",
    [UserStatus.PENDING_BANK_REACTIVATION]: "Pending Bank Reactivation",
  };

  const statusClass = {
    [UserStatus.APPROVED]: "approved",
    [UserStatus.PENDING_DIRECTOR_APPROVAL]: "pending",
    [UserStatus.PENDING_BANK_APPROVAL]: "pending",
    [UserStatus.REJECTED]: "rejected",
    [UserStatus.INACTIVE]: "inactive",
    [UserStatus.PENDING_DIRECTOR_REACTIVATION]: "reactivation",
    [UserStatus.PENDING_BANK_REACTIVATION]: "reactivation",
  }[status];

  return (
    <span className={`status-badge ${statusClass}`}>
      {labelMap[status] || "Unknown"}
    </span>
  );
};

const UserApprovals = () => {
  const [page] = useState(1);
  const [pageSize] = useState(10);

  const dispatch = useDispatch();
  const match = useRouteMatch();
  const { id } = match.params as { id: string };

  const modalHandler = (modalData: any) => {
    dispatch(
      showModal({
        modalIdentifier: "createuser",
        dataRecord: modalData,
        action: "customrange",
      }),
    );
  };

  const {
    data: approvers,
    isLoading,
    isError,
    error,
  } = useTeamMembers({ funder_id: Number(id), page, pageSize });

  let RenderedComponent = null;

  if (isLoading) {
    RenderedComponent = <Loader centered={true} text="Fetching approvals..." />;
  } else if (isError) {
    RenderedComponent = (
      <div>
        <div className="grid__padding animated fadeInRight">
          <h4 className="center-txt p-y-5">
            <i className="fas fa-bell" />{" "}
            {(error as ApiErrorState)?.response?.data?.message ||
              "An error occurred"}{" "}
          </h4>
        </div>
      </div>
    );
  } else if (!approvers || approvers?.data?.length === 0) {
    RenderedComponent = (
      <div>
        <div className="grid__padding animated fadeInRight">
          <h4 className="center-txt">
            <i className="fas fa-bell" /> No available approver data
          </h4>
        </div>
      </div>
    );
  } else {
    RenderedComponent = (
      <div className="row m-x-sm m-y-2 grid__padding">
        <MDBTable responsive minimal="true" bordered hover>
          <MDBTableHead>
            <tr>
              <th>Name</th>
              <th>Role</th>
              <th>Email Address</th>
              <th>Phone Number</th>
              <th>Status</th>
              <th>Action</th>
            </tr>
          </MDBTableHead>
          <MDBTableBody>
            {approvers?.data.map((user, _id) => {
              return (
                <tr key={user?.id}>
                  <td>{`${user?.firstName} ${user?.lastName}`}</td>
                  <td>{renderRoles(user)}</td>
                  <td>{user?.email}</td>
                  <td>{user?.phoneNumber}</td>
                  <td>{renderStatus(user?.status as UserStatus)}</td>
                  <td>
                    <button
                      type="button"
                      className={styles.temporaryPinAction}
                      onClick={() =>
                        modalHandler({
                          type: "view_user",
                          ssoUserId: user?.ssoUserId,
                        })
                      }
                    >
                      Temporary Pin
                    </button>
                  </td>
                </tr>
              );
            })}
          </MDBTableBody>
        </MDBTable>
      </div>
    );
  }

  return <div>{RenderedComponent}</div>;
};

export default UserApprovals;
